/**
 * CallRecordingsInline — iter101 · Compact list of call recordings
 * displayed **inline** on a candidate card (Growth, list view, etc.).
 *
 * CEO 2026-09-06 : « Je veux pouvoir écouter les 1, 2 ou 3 appels
 * directement à côté du nom du candidat, sans ouvrir sa fiche. »
 *
 * Fetches `/twilio/calls?candidate_id={id}` on mount, renders one
 * compact `<audio>` per call that has a `recording_url`. Uses blob
 * URL fetched via `/twilio/calls/{id}/recording` (Bearer-authed).
 */
import { AlertCircle, Phone, PhoneMissed } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { API_BASE, tokenStore } from "../../lib/api";

const STATUS_META = {
    completed: { label: "Terminé", icon: Phone,       tone: "text-emerald-600" },
    "no-answer": { label: "Sans réponse", icon: PhoneMissed, tone: "text-slate-500" },
    busy:      { label: "Occupé",  icon: PhoneMissed, tone: "text-amber-600" },
    failed:    { label: "Échec",   icon: AlertCircle, tone: "text-shariti-red" },
    canceled:  { label: "Annulé",  icon: PhoneMissed, tone: "text-slate-500" },
};

function formatDuration(sec) {
    if (!sec && sec !== 0) return "—";
    const s = Math.round(sec);
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m${String(s % 60).padStart(2, "0")}`;
}

function formatWhen(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch (_) { return ""; }
}

/**
 * Authenticated audio playback — the Bearer header can't sit on an
 * `<audio src>` so we fetch the MP3 via axios/fetch, turn it into a
 * blob URL, and hand that to <audio>. Revoked on unmount.
 * Fetch is triggered ONLY when the user clicks "Écouter" to save
 * bandwidth on lists.
 */
function LazyAudio({ callLogId, testid }) {
    const [src, setSrc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const urlRef = useRef(null);

    const start = useCallback(async () => {
        if (src || loading) return;
        setLoading(true);
        setError(null);
        try {
            const token = tokenStore.get();
            const res = await fetch(`${API_BASE}/twilio/calls/${callLogId}/recording`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            urlRef.current = url;
            setSrc(url);
        } catch (e) {
            setError(e.message || "Impossible de charger l'enregistrement");
        } finally {
            setLoading(false);
        }
    }, [callLogId, src, loading]);

    useEffect(() => () => {
        if (urlRef.current) {
            URL.revokeObjectURL(urlRef.current);
            urlRef.current = null;
        }
    }, []);

    if (src) {
        return (
            <audio
                data-testid={testid}
                controls
                autoPlay
                src={src}
                className="w-full h-8 rounded"
                preload="metadata"
            />
        );
    }
    return (
        <button
            type="button"
            onClick={start}
            disabled={loading}
            data-testid={`${testid}-play`}
            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-shariti-orange hover:text-shariti-orange-hover disabled:opacity-50 transition"
        >
            {loading ? "Chargement…" : "▶ Écouter"}
            {error && <span className="text-shariti-red font-medium normal-case tracking-normal ml-2">· {error}</span>}
        </button>
    );
}

export default function CallRecordingsInline({ candidateId, maxCalls = 3, className = "" }) {
    const [calls, setCalls] = useState(null);  // null = loading, [] = none
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const token = tokenStore.get();
                const res = await fetch(`${API_BASE}/twilio/calls?candidate_id=${candidateId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (cancelled) return;
                setCalls(Array.isArray(data) ? data : []);
            } catch (e) {
                if (!cancelled) setError(e.message || "Erreur");
            }
        })();
        return () => { cancelled = true; };
    }, [candidateId]);

    if (calls === null && !error) return null;  // still loading, silent
    if (error) return null;                       // silent — the card stays clean
    if (!calls?.length) return null;              // no calls → hide

    const withAudio = calls.filter((c) => c.recording_url).slice(0, maxCalls);
    if (!withAudio.length) return null;

    return (
        <div className={`mt-3 rounded-xl border border-slate-200 bg-slate-50/50 p-2.5 ${className}`}>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
                {withAudio.length === 1 ? "Enregistrement du dernier appel" : `${withAudio.length} appels enregistrés`}
            </div>
            <ul className="space-y-2">
                {withAudio.map((call) => {
                    const meta = STATUS_META[call.status] || { label: call.status, tone: "text-slate-500", icon: Phone };
                    const Icon = meta.icon;
                    return (
                        <li key={call.id} className="rounded-lg bg-white border border-slate-200 p-2">
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                                <div className="flex items-center gap-2">
                                    <Icon className={`h-3.5 w-3.5 ${meta.tone}`} />
                                    <span className="text-[11px] font-black text-slate-700">
                                        {formatWhen(call.created_at)}
                                    </span>
                                    <span className="text-[10px] font-semibold text-slate-500">
                                        · {formatDuration(call.recording_duration_sec || call.duration_sec)}
                                    </span>
                                </div>
                                <span className={`text-[9px] font-black uppercase tracking-wider ${meta.tone}`}>
                                    {meta.label}
                                </span>
                            </div>
                            <LazyAudio callLogId={call.id} testid={`call-inline-audio-${call.id}`} />
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
